import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import {
	ArrowLeftIcon,
	DownloadCloud02Icon,
	ImageDownIcon,
	ImageUpIcon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { ManualRetouchingCard } from '@/features/photoset/components/manual-retouching-card'
import { ManualRetouchingUploader } from '@/features/photoset/components/manual-retouching-uploader'
import {
	PhotosetStepCardsContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useUpdateStepToNeedsReview } from '@/features/photoset/queries/use-update-step-to-needs-review'
import { useUpdateStepToRetouchingRevision } from '@/features/photoset/queries/use-update-step-to-retouching-revision'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetRetouchingRevisionUploadStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [isGenerationThumbsCompleted, setIsGenerationThumbsCompleted] =
		useState(false)

	// queries
	const { data: photoset } = useGetPhotoset(
		{ id: urlParams.id },
		{
			refetchInterval: () =>
				isGenerationThumbsCompleted ? false : 10_000
		}
	)

	// mutations
	const {
		mutate: updateStepToNeedsReview,
		isPending: isPendingUpdateStepToNeedsReview
	} = useUpdateStepToNeedsReview({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
		}
	})

	const updateStepToRetouchingRevisionMutation =
		useUpdateStepToRetouchingRevision({
			onError: (error) => errorHandler(error),
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PHOTOSET]
				})
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
				})
			}
		})

	useEffect(() => {
		if (photoset?.need_retouching_zip_url) {
			setIsGenerationThumbsCompleted(true)
		}
	}, [photoset?.need_retouching_zip_url])

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:retouching_revision.upload_subtitle')}
				title={t('photoset:retouching_revision.title')}
			/>
			<PhotosetStepWrapper>
				<PhotosetStepCardsContainer>
					<ManualRetouchingCard
						endSlot={
							<div className='flex flex-col items-center gap-3 [&>p]:max-w-[365px]'>
								<ButtonLinkLegacy
									disabled={
										!photoset?.need_retouching_zip_url
									}
									to={photoset?.need_retouching_zip_url ?? {}}
								>
									{photoset?.need_retouching_zip_url ? (
										<>
											<DownloadCloud02Icon size={20} />
											<span>
												{t('common:download_image', {
													count: photoset.need_retouching_zip_images_count
												})}
											</span>
										</>
									) : (
										<>
											<Spinner size={20} />
											<span>
												{t('common:creating_zip')}
											</span>
										</>
									)}
								</ButtonLinkLegacy>
								<p>{t('common:download_pending_paragraph')}</p>
							</div>
						}
						icon={<ImageDownIcon size={40} />}
						paragraph={t(
							'photoset:retouching_revision.download_paragraph'
						)}
						subtitle={t(
							'photoset:retouching_revision.download_subtitle'
						)}
						title={t('common:download_title')}
					/>

					<ManualRetouchingCard
						endSlot={
							<ManualRetouchingUploader
								isDisabled={!photoset?.need_retouching_zip_url}
								onUploadCb={
									updateStepToRetouchingRevisionMutation.mutateAsync
								}
								photosetId={urlParams.id}
							/>
						}
						icon={<ImageUpIcon size={40} />}
						paragraph={t('common:upload_jpgs_name_info')}
						subtitle={t(
							'photoset:retouching_revision.upload_subtitle'
						)}
						title={t('common:upload_retouched_images')}
					/>
				</PhotosetStepCardsContainer>

				<PageFooter
					startSlot={
						<ButtonLegacy
							disabled={
								isPendingUpdateStepToNeedsReview ||
								isPendingUpdateStepToNeedsReview
							}
							onClick={() =>
								updateStepToNeedsReview(urlParams.id)
							}
							type='button'
							variant='tertiary'
						>
							<ArrowLeftIcon />
							{t('photoset:needs_review.title')}
						</ButtonLegacy>
					}
				/>
			</PhotosetStepWrapper>
		</>
	)
}
